// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Firebase Auth user
  const [usuario, setUsuario] = useState(null); // Firestore user
  const [loading, setLoading] = useState(true);
  const [loadingUserDoc, setLoadingUserDoc] = useState(true);

  useEffect(() => {
    console.log('AuthContext montado. Iniciando onAuthStateChanged...');

    let unsubscribeFirestore = null;
    let unsubscribeImoveis = null;

    // ✅ agora são 2 listeners para sócios e 2 para parcerias (sem query global)
    let unsubscribeSociosCriador = null;
    let unsubscribeSociosConvidado = null;
    let unsubscribeParceriasFrom = null;
    let unsubscribeParceriasTo = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (userAuth) => {
      console.log(
        '🔥 onAuthStateChanged disparado:',
        userAuth ? userAuth.uid : 'null (usuário deslogado)'
      );

      setUser(userAuth);
      setLoading(false);

      // helper pra limpar tudo com segurança
      const cleanupAll = () => {
        if (unsubscribeFirestore) unsubscribeFirestore();
        if (unsubscribeImoveis) unsubscribeImoveis();

        if (unsubscribeSociosCriador) unsubscribeSociosCriador();
        if (unsubscribeSociosConvidado) unsubscribeSociosConvidado();
        if (unsubscribeParceriasFrom) unsubscribeParceriasFrom();
        if (unsubscribeParceriasTo) unsubscribeParceriasTo();

        unsubscribeFirestore = null;
        unsubscribeImoveis = null;

        unsubscribeSociosCriador = null;
        unsubscribeSociosConvidado = null;
        unsubscribeParceriasFrom = null;
        unsubscribeParceriasTo = null;
      };

      if (!userAuth) {
        console.log('🙅‍♂️ Usuário deslogado');
        cleanupAll();
        setUsuario(null);
        setUser(null);
        setLoadingUserDoc(false);
        return;
      }

      // ✅ usuário logado
      setLoadingUserDoc(true);

      const userDocRef = doc(db, 'users', userAuth.uid);

      unsubscribeFirestore = onSnapshot(
        userDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log('📥 Dados do Firestore:', data);

            const usuarioBase = {
              uid: userAuth.uid,
              email: userAuth.email,
              nome: data.nome || userAuth.displayName || '',
              status: data.status || 'ativo',
              cadastroCompleto: data.cadastroCompleto ?? false,
              role: data.role || 'corretor',
              ...data,
            };

            setUsuario((prev) => ({
              ...usuarioBase,
              imoveis: prev?.imoveis || 0,
              socios: prev?.socios || 0,
              parcerias: prev?.parcerias || 0,
            }));
          } else {
            console.log('📭 Documento do usuário não existe:', userAuth.uid);

            const usuarioBasico = {
              uid: userAuth.uid,
              email: userAuth.email,
              nome: userAuth.displayName || '',
              status: 'ativo',
              cadastroCompleto: false,
              role: 'corretor',
            };

            setUsuario((prev) => ({
              ...usuarioBasico,
              imoveis: prev?.imoveis || 0,
              socios: prev?.socios || 0,
              parcerias: prev?.parcerias || 0,
            }));
          }

          setLoadingUserDoc(false);
        },
        (error) => {
          console.error('❌ Erro no onSnapshot do Firestore (users):', error);
          setUsuario(null);
          setLoadingUserDoc(false);
        }
      );

      // ✅ Contador de imóveis do usuário
      unsubscribeImoveis = onSnapshot(
        query(collection(db, 'imoveis'), where('ownerId', '==', userAuth.uid)),
        (snapshot) => {
          setUsuario((prev) =>
            prev ? { ...prev, imoveis: snapshot.size } : prev
          );
        },
        (error) => {
          console.warn('⚠️ Erro listener imoveis:', error);
        }
      );

      // ✅ Sócios: sem query global
      // 1) eu como criador
      unsubscribeSociosCriador = onSnapshot(
        query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('criador', '==', userAuth.uid)
        ),
        (snapshot) => {
          const countCriador = snapshot.size;

          setUsuario((prev) => {
            const other = prev?.socios_convidado_count || 0;
            return prev
              ? {
                  ...prev,
                  socios: countCriador + other,
                  socios_criador_count: countCriador,
                }
              : prev;
          });
        },
        (error) => {
          console.warn('⚠️ Erro listener socios (criador):', error);
        }
      );

      // 2) eu como convidado
      unsubscribeSociosConvidado = onSnapshot(
        query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('convidado', '==', userAuth.uid)
        ),
        (snapshot) => {
          const countConvidado = snapshot.size;

          setUsuario((prev) => {
            const other = prev?.socios_criador_count || 0;
            return prev
              ? {
                  ...prev,
                  socios: countConvidado + other,
                  socios_convidado_count: countConvidado,
                }
              : prev;
          });
        },
        (error) => {
          console.warn('⚠️ Erro listener socios (convidado):', error);
        }
      );

      // ✅ Parcerias: sem query global
      // 1) eu como from
      unsubscribeParceriasFrom = onSnapshot(
        query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('from', '==', userAuth.uid)
        ),
        (snapshot) => {
          const countFrom = snapshot.size;

          setUsuario((prev) => {
            const other = prev?.parcerias_to_count || 0;
            return prev
              ? {
                  ...prev,
                  parcerias: countFrom + other,
                  parcerias_from_count: countFrom,
                }
              : prev;
          });
        },
        (error) => {
          console.warn('⚠️ Erro listener parcerias (from):', error);
        }
      );

      // 2) eu como to
      unsubscribeParceriasTo = onSnapshot(
        query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('to', '==', userAuth.uid)
        ),
        (snapshot) => {
          const countTo = snapshot.size;

          setUsuario((prev) => {
            const other = prev?.parcerias_from_count || 0;
            return prev
              ? {
                  ...prev,
                  parcerias: countTo + other,
                  parcerias_to_count: countTo,
                }
              : prev;
          });
        },
        (error) => {
          console.warn('⚠️ Erro listener parcerias (to):', error);
        }
      );
    });

    return () => {
      console.log('🧹 Limpando listeners (AuthProvider unmount)...');
      unsubscribeAuth();

      if (unsubscribeFirestore) unsubscribeFirestore();
      if (unsubscribeImoveis) unsubscribeImoveis();

      if (unsubscribeSociosCriador) unsubscribeSociosCriador();
      if (unsubscribeSociosConvidado) unsubscribeSociosConvidado();

      if (unsubscribeParceriasFrom) unsubscribeParceriasFrom();
      if (unsubscribeParceriasTo) unsubscribeParceriasTo();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, usuario, loading, loadingUserDoc }}>
      {children}
    </AuthContext.Provider>
  );
};
